//@ts-ignore
import localStorage = require("../../ServiceDesk/sdServices/local-storage");
import { StorageWrapperService, memoryLeakEntries } from "../../ServiceDesk/sdServices/local-storage-wrapper-service";
import {
  nonStandardPushInformationEvent,
  nonStandardPushErrorEvent
} from "../../ServiceDesk/sdServices/non-standard-logging";
import { IndexedDbStorageWrapper } from "./indexeddb-storage-wrapper";
import { getFlightDetails } from "./window-communication-bridge-flighting";
import {
  WindowHeartbeatService,
  WindowStatus,
  open,
  closed,
  unexpectedlyClosed,
  getInstance as WindowHeartbeatServiceInstance
} from "./window-heartbeat-service";
import { v4 } from "uuid";

export interface CommunicationOptions {
  /**
   * Will not call any subscribers that have been registered on the same window that is doing the publish.
   */
  ignoreCurrentWindow?: boolean;
  /**
   * By default WCB will communicate with all windows. If you'd like to force it to only talk to specific windows, then supply
   * the window ids here. If those window Ids are not found, then an exception will be thrown.
   */
  targetedWindowIds?: string[];
  /**
   * Will also add a promise to the return method which will wait for the results object to be returned from all the other windows.
   * It will have information regarding how many callbacks were hit and any errors that occurred.
   */
  waitForResults?: boolean;
}
export interface WindowCommunicationBridgeService {
  /**
   * Publishes a message with an event name to all windows. Will call all subscribers to that event
   * name and supply as a parameter the params. If you pass the option waitForRsults, you can also access
   * a second promise on the publish result object.
   *
   * Return type of Promise<any> is DEPRECATED. Please use PublishResult instead.
   *
   * Note: Use of the Promise directly from the publish method is deprecated. Instead you use the PublishResult object
   * which contains the messageAcknowledged promise instead.
   */
  publish: (eventName: string, params: any, options?: CommunicationOptions) => Promise<any> & PublishResult;

  /**
   * Subscribes to an event name. with a callback method. That callback will be hit once
   * another window decides to publish an event. Scope is required so that on scope destroy the
   * callback is unregistered.
   */
  subscribe: (eventName: string, callback: (params: any) => void, scope: ng.IScope) => void;

  /**
   * Get the current window ID. Useful if using the options for targetedWindowIds.
   */
  getWindowId: () => string;

  /**
   * Enables polling for events by default its configured for storage events which is currently
   * under flight
   */
  enablePollingForEvents: (enabled: boolean) => void;
}

/** DON'T USE THIS. Only for use in non-angular code! */
export interface StandaloneWindowCommunicationBridgeService {
  publish: (eventName: string, params: any, options?: CommunicationOptions) => Promise<any> & PublishResult;
  subscribe: (eventName: string, callback: (params: any) => void) => () => void;
  unsubscribe: (eventName: string, callback: (params: any) => void) => void;
  getWindowId: () => string;
  enablePollingForEvents: (enabled: boolean) => void;
}

let bridge: WindowCommunicationBridgeStandalone = undefined;
let alwaysPollForEvents = false;

export function getWindowsCommunicationBridgeInstance(
  pollForEvents?: boolean
): StandaloneWindowCommunicationBridgeService {
  if (!bridge) {
    bridge = new WindowCommunicationBridgeStandalone(
      StorageWrapperService.getInstance(),
      pollForEvents || alwaysPollForEvents
    );
  }
  return bridge;
}

export function enablePollingForEvents(enabled: boolean) {
  alwaysPollForEvents = enabled;
  if (bridge) {
    bridge.enablePollingForEvents(enabled);
  }
}

export interface SubscriberResults {
  /** The number of subscribers across all windows notified that were listening to this event */
  subscribers: number;
  /** a list of errors (if any) that were returned from calling callbacks registered to the event. */
  errors?: string[];
}

export interface PublishResult {
  /**
   * The promise which will be returned if CommunicationOptions.waitForResults was specified. Results will contain statistics on all subscribers
   * hit for the event published including any errors. Results is resolved once all callbacks on all notified windows have responded.
   */
  callbackResultsPromise?: Promise<SubscriberResults>;

  /**
   * Promise resolved when all windows have acknowledged receipt of the publish event and have began, but not necessarily finished executing callbacks.
   */
  messageAcknowledged: Promise<void>;
}

export const windowNotificationPrefix = "wcbNotify";
export const windowEventPrefix = "wcbEvent";
export const windowAcknowledgePrefix = "wcbAck";
export const windowResultsPrefix = "wcbResults";
const tryWaitTime = 1000;
const relaxedTryWaitTime = 3000;
const resultsWaitTime = 10000;
const defaultMaxRetries = 3;
const expiredEventTime = 30000;
const telemetryAggregationInterval = 600000;
const maxEventSize = 1; // 1 KB max size
const defaultStoragePollingFrequency = 750;
const callbackErrorMaxLength = 500;

interface StorageObject {
  params: any;
  eventName: string;
  options?: CommunicationOptions;
}

interface NotificationKeyDetails {
  messageId: string;
  timeStamp: number;
  windowId: string;
  originatingTimeStamp: number;
  originatingWindowId: string;
  notificationNumber: number;
}

interface CleanUpEntries {
  allKeys: Set<string>;
  expiredEventKey: string;
  notifications: Set<string>;
}

enum MessageType {
  Notification,
  Ack,
  Event,
  Results,
  Unknown
}

class TelemetryStatistics {
  totalNotifications = 0;
  totalSucceededNotifications = 0;
  totalFailedNotifications = 0;
  totalAckDuration = 0;
  totalReceivedAcks = 0;
  totalLatentAcks = 0;
  targetedWindowCount = 0;
  done = false;
  communicationSucceeded = false;
  resultsSucceeded = false;
  resultsRequested = false;
  messageAttempts = 0;
  startTime = new Date().getTime();
  endTime: number;
  communicationSucceededDueToNoWindows = false;
  acknowledgedWindowIds: string[];

  constructor(
    public eventName: string,
    public paramSize: number,
    public storageNotificationMethod: StorageNotificationMethod,
    options: CommunicationOptions
  ) {
    if (options) {
      if (options.targetedWindowIds) {
        this.targetedWindowCount = options.targetedWindowIds.length;
      }
      if (options.waitForResults) {
        this.resultsRequested = true;
      }
    }
  }
}

class TelemetryEventStatistics {
  eventTotalParamSize = 0;
  eventAvgParamSize = 0;
  eventMaxParamSize = 0;
  eventTotalCommunications = 0;
  eventTotalNotifications = 0;
  eventTotalFailedNotifications = 0;
  eventTotalSucceededNotifications = 0;
  eventTotalFailedCommunications = 0;
  eventTotalSucceededCommunications = 0;
  eventTotalSucceededCommunicationsDueToNoWindows = 0;
}

type StorageNotificationMethod = "Polling" | "PollingAndEventing" | "Eventing";

class PublishDetail {
  promiseAckResolve: () => void;
  promiseAckReject: (reason?: any) => void;
  promiseResultResolve: (results: SubscriberResults) => void;
  public awaitingResultsTimeout: number;
  public resultsReceptionKeys: string[] = [];
  public awaitingResultsWindowIds = new Set<string>();
  public notifiedWindowIds: Set<string>;
  public currentTryAckReceptionWindowIds: Set<string>;
  public allAckReceptionWindowIds: Set<string>;
  public ackReceptionKeys: Set<string>;
  public windowNotifyKeys: string[];
  public dateCreated: Date;
  public results: SubscriberResults[] = [];

  constructor(
    public id: string,
    public mainEventKey: string,
    public eventName: string,
    public options: CommunicationOptions
  ) {
    this.currentTryAckReceptionWindowIds = new Set<string>();
    this.ackReceptionKeys = new Set<string>();
    this.allAckReceptionWindowIds = new Set<string>();
    this.dateCreated = new Date();
  }
}

class SubscriptionMessageDetail {
  receivedNotifications = new Set<number>();
  eventName: string;

  constructor(notificationNumber: number) {
    this.receivedNotifications.add(notificationNumber);
  }
}

interface SubscriptionMessageExpiryDetail {
  expiryTime: number;
  messageId: string;
}

export class WindowCommunicationBridge implements WindowCommunicationBridgeService {
  private subscriptions: { [eventName: string]: ((params: any) => any)[] } = {};
  private publishDetails: { [eventId: string]: PublishDetail } = {};
  private subscriptionDetails: { [eventId: string]: SubscriptionMessageDetail } = {};
  private windowId: string;
  private telemetryStatistics: { [messageId: string]: TelemetryStatistics };
  private tryIntervalTime: number;
  private maxRetries: number;
  private storagePollingFrequency: number;
  private storageNotificationMethod: StorageNotificationMethod = "Polling";
  private subscriptionMessageExpiryDetails: SubscriptionMessageExpiryDetail[] = [];
  private alternatePollingStorage: Storage;
  private publishingIndexedDB = false;
  private regularInterval: number;

  constructor(
    private storage: Storage,
    private windowsHeartbeatService: WindowHeartbeatService,
    tryIntervalTime: number,
    private pollForEvents: boolean
  ) {
    this.windowId = windowsHeartbeatService.getCurrentWindowId();
    this.tryIntervalTime = tryIntervalTime ? tryIntervalTime : tryWaitTime;
    this.maxRetries = defaultMaxRetries;
    this.storagePollingFrequency = defaultStoragePollingFrequency;
    this.telemetryStatistics = {};
    window.setInterval(this.aggregateTelemetry.bind(this), telemetryAggregationInterval, "telemetryInterval");
    window.setInterval(
      this.cleanUpExpiredSubscriptionDetails.bind(this),
      expiredEventTime * 2,
      "expiredSubscriptionDetailInterval"
    );
    this.registerStorageListeningMethod();
    this.cleanUpExpiredEntries();
  }

  private cleanUpExpiredSubscriptionDetails() {
    let time = new Date().getTime();
    while (this.subscriptionMessageExpiryDetails.length > 0) {
      if (this.subscriptionMessageExpiryDetails[0].expiryTime < time) {
        let expiryDetail = this.subscriptionMessageExpiryDetails.shift();
        delete this.subscriptionDetails[expiryDetail.messageId];
      } else {
        break;
      }
    }
  }

  private registerStorageListeningMethod() {
    // Once disabling polling is flighted and no issues reported this interval can be removed
    this.regularInterval = window.setInterval(
      this.pollStorage.bind(this),
      this.storagePollingFrequency,
      "pollingInterval"
    );

    // storageevent is enabled by default.
    this.storageNotificationMethod = "PollingAndEventing";
    let listenerFn = (storageEvent: StorageEvent) => {
      this.onStorageEvent(storageEvent);
    };
    window.addEventListener("storage", listenerFn, false);

    getFlightDetails().then((wcbFlightInfo) => {
      if (wcbFlightInfo.relaxedTryWaitTime) {
        this.tryIntervalTime = relaxedTryWaitTime;
      }
      if (wcbFlightInfo.disableRetries) {
        this.maxRetries = 0;
        this.tryIntervalTime = relaxedTryWaitTime * defaultMaxRetries;
      }

      if (!this.pollForEvents && wcbFlightInfo.useOnlyEventing) {
        this.clearPollingStorage();
      }

      let indexedDBStorage: Storage;
      if (wcbFlightInfo.receieveIndexedDB) {
        indexedDBStorage = new IndexedDbStorageWrapper();
        indexedDBStorage.whenReady().then(() => {
          this.alternatePollingStorage = indexedDBStorage;
        });
      }
      if (wcbFlightInfo.publishIndexedDB) {
        if (!indexedDBStorage) {
          indexedDBStorage = new IndexedDbStorageWrapper();
        }
        indexedDBStorage.whenReady().then(() => {
          // copy anything existing in localStorage to indexeddb for the switch.
          memoryLeakEntries().forEach((value, key) => {
            let messageType = this.getMessageType(key);
            if (messageType != MessageType.Unknown) {
              indexedDBStorage.setItem(key, value);
            }
          });
          this.alternatePollingStorage = this.storage;
          this.storage = indexedDBStorage;
          this.publishingIndexedDB = true;
          this.clearPollingStorage();
          indexedDBStorage.registerAfterPollCallback(this.pollStorage.bind(this));
          this.cleanUpExpiredEntries();
        });
      }
    });
  }

  private clearPollingStorage() {
    if (this.regularInterval) {
      window.clearInterval(this.regularInterval);
    }
    this.regularInterval = undefined;
  }

  public enablePollingForEvents(enabled: boolean) {
    localStorage.overrideUseLocalStorageKey = enabled;
    this.pollForEvents = enabled;
    if (enabled) {
      if (!this.regularInterval) {
        this.regularInterval = window.setInterval(
          this.pollStorage.bind(this),
          this.storagePollingFrequency,
          "pollingInterval"
        );
      }
    } else {
      this.clearPollingStorage();
    }
  }

  private pollStorage() {
    this.storage.getKeys().forEach((key: string) => {
      if (this.isWCBKey(key)) {
        this.onStorageEvent({ key: key, newValue: this.storage.getItem(key) });
      }
    });
    if (this.alternatePollingStorage) {
      this.alternatePollingStorage.entries(this.alternatePollingStorage).forEach((value: string, key: string) => {
        this.onStorageEvent({ key: key, newValue: value });
      });
    }
  }

  private aggregateEventTelemetry() {
    let eventStats: { [eventName: string]: TelemetryEventStatistics } = {};
    Object.keys(this.telemetryStatistics).forEach((id) => {
      let stats = this.telemetryStatistics[id];
      if (stats.done) {
        let eventStat: TelemetryEventStatistics = eventStats[stats.eventName] || new TelemetryEventStatistics();
        if (stats.paramSize > eventStat.eventMaxParamSize) {
          eventStat.eventMaxParamSize = stats.paramSize;
        }
        eventStat.eventTotalParamSize += stats.paramSize;
        eventStat.eventTotalFailedNotifications += stats.totalFailedNotifications;
        eventStat.eventTotalSucceededNotifications += stats.totalSucceededNotifications;
        eventStat.eventTotalNotifications += stats.totalNotifications;
        eventStat.eventTotalCommunications++;
        eventStat.eventTotalFailedCommunications = stats.communicationSucceeded
          ? eventStat.eventTotalFailedCommunications
          : ++eventStat.eventTotalFailedCommunications;
        eventStat.eventTotalSucceededCommunications = stats.communicationSucceeded
          ? ++eventStat.eventTotalSucceededCommunications
          : eventStat.eventTotalSucceededCommunications;
        eventStat.eventTotalSucceededCommunicationsDueToNoWindows = stats.communicationSucceededDueToNoWindows
          ? ++eventStat.eventTotalSucceededCommunicationsDueToNoWindows
          : eventStat.eventTotalSucceededCommunicationsDueToNoWindows;
        eventStats[stats.eventName] = eventStat;
      }
    });
    Object.keys(eventStats).forEach((eventName) => {
      let eventStat = <any>eventStats[eventName];
      eventStat.eventAvgParamSize = this.toPrettyKB(
        this.coalesceNonNumberToZero(eventStat.eventTotalParamSize / eventStat.eventTotalCommunications)
      );
      eventStat.eventTotalParamSize = this.toPrettyKB(eventStat.eventTotalParamSize);
      eventStat.eventMaxParamSize = this.toPrettyKB(eventStat.eventMaxParamSize);
    });
    return eventStats;
  }

  private aggregateTelemetry() {
    let totalAttempts = 0,
      totalSuccessOnFirstAttempt = 0,
      totalSuccessOnSecondAttempt = 0,
      totalSuccessOnThirdAttempt = 0,
      totalParamSize = 0,
      totalCommunications = 0,
      totalFailedCommunications = 0,
      totalSucceededCommunications = 0,
      maxParamSize = 0,
      maxNameSize = 0,
      totalSize = 0,
      localStorageSize = 0,
      totalSucceededNotifications = 0,
      totalFailedNotifications = 0,
      totalNotifications = 0,
      totalSuccessTime = 0,
      totalSucceededCommunicationsWithPolling = 0,
      totalCommunicationsWithPolling = 0,
      totalCommunicationsWithPollingAndEventing = 0,
      totalSucceededCommunicationsWithPollingAndEventing = 0,
      totalSucceededCommunicationsWithoutPolling = 0,
      totalCommunicationsWithoutPolling = 0,
      totalLatentAcks = 0,
      totalAcks = 0,
      totalCompletedAckTime = 0,
      totalCommunicationsSucceededDueToNoWindows = 0,
      totalCommunicationsWithTargetedWindows = 0,
      totalSucceededCommunicationsWithTargetedWindows = 0,
      totalFailedCommunicationsWithTargetedWindows = 0,
      totalCommunicationsWithResultsRequested = 0,
      totalCommunicationsWithFailedResults = 0;
    let maxParamName: string, maxName: string;
    if (Object.keys(this.telemetryStatistics).length == 0) {
      return;
    }
    let keysToDelete: string[] = [];
    let uniqueWindowAcks = new Set<string>();
    Object.keys(this.telemetryStatistics).forEach((id) => {
      let stats = this.telemetryStatistics[id];
      if (stats.done) {
        keysToDelete.push(id);
        if (stats.eventName.length > maxNameSize) {
          maxName = stats.eventName;
        }
        let paramSize = stats.paramSize;
        if (paramSize > maxParamSize) {
          maxParamSize = paramSize;
          maxParamName = stats.eventName;
        }
        totalAcks += stats.totalReceivedAcks;
        totalCompletedAckTime += stats.totalAckDuration;
        totalLatentAcks += stats.totalLatentAcks;
        totalParamSize += paramSize;
        totalSize += this.toKB(stats.eventName.length) + paramSize;
        totalCommunications++;
        if (stats.storageNotificationMethod == "Polling") {
          ++totalCommunicationsWithPolling;
          totalSucceededCommunicationsWithPolling += stats.communicationSucceeded ? 1 : 0;
        } else if (stats.storageNotificationMethod == "PollingAndEventing") {
          ++totalCommunicationsWithPollingAndEventing;
          totalSucceededCommunicationsWithPollingAndEventing += stats.communicationSucceeded ? 1 : 0;
        } else {
          ++totalCommunicationsWithoutPolling;
          totalSucceededCommunicationsWithoutPolling += stats.communicationSucceeded ? 1 : 0;
        }
        totalCommunicationsSucceededDueToNoWindows += stats.communicationSucceededDueToNoWindows ? 1 : 0;
        totalFailedCommunications = stats.communicationSucceeded
          ? totalFailedCommunications
          : ++totalFailedCommunications;
        totalSucceededCommunications = stats.communicationSucceeded
          ? ++totalSucceededCommunications
          : totalSucceededCommunications;
        totalFailedNotifications += stats.totalFailedNotifications;
        totalSucceededNotifications += stats.totalSucceededNotifications;
        totalSuccessTime += stats.endTime ? stats.endTime - stats.startTime : 0;
        totalNotifications += stats.totalNotifications;
        if (stats.targetedWindowCount > 0) {
          totalSucceededCommunicationsWithTargetedWindows += stats.communicationSucceeded ? 1 : 0;
          totalFailedCommunicationsWithTargetedWindows += stats.communicationSucceeded ? 0 : 1;
          totalCommunicationsWithTargetedWindows++;
        }
        if (stats.resultsRequested) {
          totalCommunicationsWithResultsRequested++;
          totalCommunicationsWithFailedResults += stats.resultsSucceeded ? 0 : 1;
        }

        if (stats.messageAttempts == 1) {
          totalSuccessOnFirstAttempt++;
        } else if (stats.messageAttempts == 2) {
          totalSuccessOnSecondAttempt++;
        } else if (stats.messageAttempts == 3 && stats.communicationSucceeded) {
          totalSuccessOnThirdAttempt++;
        }
        totalAttempts += stats.messageAttempts;
        if (stats.acknowledgedWindowIds) {
          stats.acknowledgedWindowIds.forEach((id) => uniqueWindowAcks.add(id));
        }
      }
    });

    localStorageSize = localStorage.getSize(this.storage);

    let telemetryToLog = {
      localStorageUsed: this.toPrettyKB(localStorageSize),
      totalParamSize: this.toPrettyKB(totalParamSize),
      avgParamSize: this.toPrettyKB(this.coalesceNonNumberToZero(totalParamSize / totalCommunications)),
      maxParam: { size: this.toPrettyKB(maxParamSize), name: maxParamName },
      maxName: { size: this.toPrettyKB(maxNameSize), name: maxName },
      totalNotifications: totalNotifications,
      totalFailedNotifications: totalFailedNotifications,
      totalSucceededNotifications: totalSucceededNotifications,
      totalCommunications: totalCommunications,
      totalSucceededCommunications: totalSucceededCommunications,
      totalFailedCommunications: totalFailedCommunications,
      totalLatentAcks: totalLatentAcks,
      avgCompletedAckRoundtripTime: this.coalesceNonNumberToZero(totalCompletedAckTime / totalAcks),
      totalAttempts: totalAttempts,
      averageAttempts: this.coalesceNonNumberToZero(totalAttempts / totalCommunications),
      totalSuccessOnFirstAttempt: totalSuccessOnFirstAttempt,
      totalSuccessOnSecondAttempt: totalSuccessOnSecondAttempt,
      totalSuccessOnThirdAttempt: totalSuccessOnThirdAttempt,
      avgSuccessTime: this.coalesceNonNumberToZero(totalSuccessTime / totalSucceededCommunications),
      totalCommunicationsWithPollingAndEventing: totalCommunicationsWithPollingAndEventing,
      totalSucceededCommunicationsWithPollingAndEventing: totalSucceededCommunicationsWithPollingAndEventing,
      totalCommunicationsWithPolling: totalCommunicationsWithPolling,
      totalCommunicationsWithoutPolling: totalCommunicationsWithoutPolling,
      totalSucceededCommunicationsWithPolling: totalSucceededCommunicationsWithPolling,
      totalSucceededCommunicationsWithoutPolling: totalSucceededCommunicationsWithoutPolling,
      totalCommunicationsSucceededDueToNoWindows: totalCommunicationsSucceededDueToNoWindows,
      totalUniqueWindowsAcked: uniqueWindowAcks.size,
      tryIntervalTime: this.tryIntervalTime,
      storagePollingFrequency: this.storagePollingFrequency,
      maxRetries: this.maxRetries,
      totalCommunicationsWithTargetedWindows: totalCommunicationsWithTargetedWindows,
      totalSucceededCommunicationsWithTargetedWindows: totalSucceededCommunicationsWithTargetedWindows,
      totalFailedCommunicationsWithTargetedWindows: totalFailedCommunicationsWithTargetedWindows,
      totalCommunicationsWithResultsRequested: totalCommunicationsWithResultsRequested,
      totalCommunicationsWithFailedResults: totalCommunicationsWithFailedResults,
      eventBreakDown: this.aggregateEventTelemetry()
    };
    this.pushTelemetryLog(telemetryToLog);
    keysToDelete.forEach((key) => {
      delete this.telemetryStatistics[key];
    });
  }

  private coalesceNonNumberToZero(input: number) {
    return Number.isFinite(input) ? input : 0;
  }

  private toPrettyKB(charCount: number) {
    return this.toKB(charCount).toFixed(2) + " KB";
  }
  private toKB(charCount: number): number {
    return (charCount * 2) / 1024;
  }

  private getMessageType(key: string): MessageType {
    if (key.startsWith(windowAcknowledgePrefix)) {
      return MessageType.Ack;
    } else if (key.startsWith(windowNotificationPrefix)) {
      return MessageType.Notification;
    } else if (key.startsWith(windowEventPrefix)) {
      return MessageType.Event;
    } else if (key.startsWith(windowResultsPrefix)) {
      return MessageType.Results;
    }
    return MessageType.Unknown;
  }

  public cleanUpExpiredEntries() {
    getFlightDetails().then((flightDetails) => {
      let entriesByMessageId: { [id: string]: CleanUpEntries } = {};
      let acks = new Set<string>();
      let allExpiredEntries: string[] = [];
      let time = new Date().getTime();
      let entries: Map<string, string>;
      if (this.publishingIndexedDB) {
        entries = this.storage.entries();
      } else {
        entries = memoryLeakEntries();
      }
      entries.forEach((value, key) => {
        let messageType = this.getMessageType(key);
        if (messageType != MessageType.Unknown) {
          let details = this.getStorageKeyDetails(key);
          this.addKeyForMessageIdToList(key, details.messageId, entriesByMessageId);
          if (this.isExpiredEntry(details, time)) {
            allExpiredEntries.push(key);
          }
          switch (messageType) {
            case MessageType.Ack:
              acks.add(details.messageId + details.windowId);
              break;
            case MessageType.Notification:
              entriesByMessageId[details.messageId].notifications.add(details.windowId);
              break;
            case MessageType.Event:
              if (this.isExpiredEntry(details, time)) {
                entriesByMessageId[details.messageId].expiredEventKey = key;
              }
              break;
          }
        }
      });
      Object.keys(entriesByMessageId).forEach((messageId: string) => {
        let cleanUpEntries = entriesByMessageId[messageId];
        if (cleanUpEntries.expiredEventKey) {
          let found = false;
          cleanUpEntries.notifications.forEach((windowId) => {
            if (!found && !acks.has(messageId + windowId)) {
              found = true;
              let storageValue = this.storage.getItem(cleanUpEntries.expiredEventKey);
              let storageObj: StorageObject;
              if (storageValue) {
                storageObj = JSON.parse(this.storage.getItem(cleanUpEntries.expiredEventKey));
              }
              let eventName: string;
              if (storageObj && storageObj.eventName) {
                eventName = storageObj.eventName;
              }
              this.pushTelemetryLog(`Found expired event without window acknowledgement for event ${eventName}`);
            }
          });
          this.cleanUpEntries(Array.from(cleanUpEntries.allKeys));
        }
      });
      this.cleanUpEntries(allExpiredEntries);
    });
  }

  private isExpiredEntry(detail: NotificationKeyDetails, timeNow: number) {
    return detail.timeStamp < timeNow - expiredEventTime;
  }

  private addKeyForMessageIdToList(key: string, messageId: string, map: { [id: string]: CleanUpEntries }) {
    if (!map[messageId]) {
      map[messageId] = { notifications: new Set<string>(), allKeys: new Set<string>(), expiredEventKey: undefined };
    }
    map[messageId].allKeys.add(key);
  }

  public publish(eventName: string, params: any, options?: CommunicationOptions): Promise<void> & PublishResult {
    options = options || {};
    if (options.ignoreCurrentWindow && options.targetedWindowIds && options.targetedWindowIds.includes(this.windowId)) {
      return <any>(
        Promise.reject(
          new Error(
            `Target window ${this.windowsHeartbeatService.getCurrentWindowId()} was specified, however that is the publishing window and ignoreCurrentWindow was also provided. Event: ${eventName}`
          )
        )
      );
    }
    let eventDetails = this.createMainEvent(eventName, params, options);
    let notifyThroughStorageResults = this.notifyThroughStorage(eventDetails, 1);
    let callbacksPromise = new Promise<SubscriberResults>((resolve, reject) => {
      if (
        !options.ignoreCurrentWindow &&
        (!options.targetedWindowIds || options.targetedWindowIds.includes(this.windowId))
      ) {
        return this.executeCallbacks(eventName, params).then((result) => resolve(result));
      }
      resolve({ subscribers: 0 });
    });
    let publishResult: PublishResult = {
      messageAcknowledged: notifyThroughStorageResults.messageAcknowledged
    };
    if (options.waitForResults) {
      publishResult.callbackResultsPromise = Promise.all([
        notifyThroughStorageResults.callbackResultsPromise,
        callbacksPromise
      ]).then((results) => {
        return this.aggregateResults(results);
      });
    }
    if ((<any>window).angular) {
      return angular.extend(notifyThroughStorageResults.messageAcknowledged, publishResult);
    } else {
      return <any>publishResult;
    }
  }

  private aggregateResults(results: SubscriberResults[]): SubscriberResults {
    return results.reduce(
      (previous, current) => {
        if (!previous.errors && current.errors) {
          previous.errors = [];
        }
        previous.subscribers += current.subscribers;
        if (current.errors) {
          previous.errors = previous.errors.concat(current.errors);
        }
        return previous;
      },
      { subscribers: 0 }
    );
  }

  public onStorageEvent(storageEvent: any) {
    if (storageEvent && storageEvent.newValue && storageEvent.key) {
      let key: string = storageEvent.key;
      if (key.startsWith(windowAcknowledgePrefix)) {
        this.handleAcknowledgeEvent(key, storageEvent.newValue);
      } else if (key.startsWith(windowNotificationPrefix)) {
        this.handleNotificationEvent(key, storageEvent.newValue);
      } else if (key.startsWith(windowResultsPrefix)) {
        this.handleResultsEvent(key, storageEvent.newValue);
      }
    }
  }

  private isWCBKey(key: string) {
    return (
      key.startsWith(windowAcknowledgePrefix) ||
      key.startsWith(windowNotificationPrefix) ||
      key.startsWith(windowResultsPrefix) ||
      false
    );
  }

  public handleResultsEvent(key: string, resultsString: string) {
    let notificationKeyDetails = this.getStorageKeyDetails(key);
    if (notificationKeyDetails.originatingWindowId == this.windowId) {
      let details = this.publishDetails[notificationKeyDetails.messageId];
      if (details) {
        details.resultsReceptionKeys.push(key);
        if (details.awaitingResultsWindowIds.delete(notificationKeyDetails.windowId)) {
          details.results.push(JSON.parse(resultsString));
        }
        if (details.awaitingResultsWindowIds.size == 0) {
          window.clearTimeout(details.awaitingResultsTimeout);
          details.promiseResultResolve(this.aggregateResults(details.results));
          this.telemetryStatistics[details.id].resultsSucceeded = true;
          this.cleanUpAllEntriesForEvent(details);
        }
      }
      this.storage.removeItem(key);
    }
  }

  private getStorageKeyDetails(key: string): NotificationKeyDetails {
    // prefix_windowID_timestamp_messageId_originatingWindowId(optional)_originatingTimeStamp(optional)_notificationNumber(optional)
    let [, windowId, timeStamp, messageId, originatingWindowId, originatingTimeStamp, notificationNumber] =
      key.split("_");
    let details: NotificationKeyDetails = {
      messageId: messageId,
      windowId: windowId,
      timeStamp: parseInt(timeStamp),
      originatingTimeStamp: originatingTimeStamp ? parseInt(originatingTimeStamp) : undefined,
      originatingWindowId: originatingWindowId ? originatingWindowId : undefined,
      notificationNumber: notificationNumber ? parseInt(notificationNumber) : undefined
    };
    return details;
  }

  private handleNotificationEvent(key: string, eventKey: string) {
    let notificationKeyDetails = this.getStorageKeyDetails(key);
    if (notificationKeyDetails.windowId == this.windowId) {
      this.storage.removeItem(key);
      let subscriptionMessageDetail = this.subscriptionDetails[notificationKeyDetails.messageId];
      let timeStamp = new Date().getTime();
      if (timeStamp - notificationKeyDetails.timeStamp > expiredEventTime) {
        this.pushTelemetryError(
          `window received notification after it was expired. Window: ${this.windowId}, MessageId: ${
            notificationKeyDetails.messageId
          }, Event: ${subscriptionMessageDetail ? subscriptionMessageDetail.eventName : "unknown"}`
        );
        return;
      }
      let ackResponseKey = `${windowAcknowledgePrefix}_${this.windowId}_${timeStamp}_${notificationKeyDetails.messageId}_${notificationKeyDetails.originatingWindowId}_${notificationKeyDetails.originatingTimeStamp}_${notificationKeyDetails.notificationNumber}`;
      if (subscriptionMessageDetail) {
        if (!subscriptionMessageDetail.receivedNotifications.has(notificationKeyDetails.notificationNumber)) {
          this.storage.setItem(ackResponseKey, key);
          subscriptionMessageDetail.receivedNotifications.add(notificationKeyDetails.notificationNumber);
        }
        return;
      }
      subscriptionMessageDetail = new SubscriptionMessageDetail(notificationKeyDetails.notificationNumber);
      this.subscriptionDetails[notificationKeyDetails.messageId] = subscriptionMessageDetail;

      let storageObj: StorageObject = JSON.parse(this.storage.getItem(eventKey));
      if (storageObj && storageObj.eventName) {
        this.storage.setItem(ackResponseKey, key);
        subscriptionMessageDetail.eventName = storageObj.eventName;
        this.executeCallbacks(storageObj.eventName, storageObj.params).then((result) => {
          if (storageObj.options && storageObj.options.waitForResults) {
            this.sendResults(result, notificationKeyDetails);
          }
        });
        this.subscriptionMessageExpiryDetails.push({
          messageId: notificationKeyDetails.messageId,
          expiryTime: new Date().getTime() + expiredEventTime * 2
        });
      }
    }
  }

  private sendResults(result: SubscriberResults, notificationDetails: NotificationKeyDetails) {
    let timeStamp = new Date();
    let resultResponseKey = `${windowResultsPrefix}_${this.windowId}_${timeStamp.getTime()}_${
      notificationDetails.messageId
    }_${notificationDetails.originatingWindowId}_${notificationDetails.notificationNumber}`;
    this.storage.setItem(resultResponseKey, JSON.stringify(result));
  }

  private markCommunicationSuccessful(details: PublishDetail) {
    this.telemetryStatistics[details.id].communicationSucceeded = true;
    let endTime = new Date().getTime();
    this.telemetryStatistics[details.id].endTime = endTime;
    if (!details.options.waitForResults || details.awaitingResultsWindowIds.size == 0) {
      this.cleanUpAllEntriesForEvent(details);
    }
    details.promiseAckResolve();
  }

  private handleAcknowledgeEvent(key: string, keyForNotificationEvent: string) {
    let now = new Date().getTime();
    let ackKeyDetails = this.getStorageKeyDetails(key);
    if (ackKeyDetails.originatingWindowId !== this.windowId) {
      return;
    }
    let notificationBeingAckedDetails = this.getStorageKeyDetails(keyForNotificationEvent);
    let stats = this.telemetryStatistics[ackKeyDetails.messageId];
    if (stats) {
      let ackRoundtripDuration = now - notificationBeingAckedDetails.timeStamp;
      stats.totalAckDuration = (stats.totalAckDuration || 0) + ackRoundtripDuration;
      stats.totalReceivedAcks = (stats.totalReceivedAcks || 0) + 1;
      if (ackRoundtripDuration > this.tryIntervalTime) {
        stats.totalLatentAcks = (stats.totalLatentAcks || 0) + 1;
      }
    }

    let details = this.publishDetails[ackKeyDetails.messageId];
    if (details) {
      if (!details.currentTryAckReceptionWindowIds.has(ackKeyDetails.windowId)) {
        this.telemetryStatistics[details.id].totalSucceededNotifications++;
        details.currentTryAckReceptionWindowIds.add(ackKeyDetails.windowId);
        details.allAckReceptionWindowIds.add(ackKeyDetails.windowId);
        details.ackReceptionKeys.add(key);
        if (details.currentTryAckReceptionWindowIds.size == details.notifiedWindowIds.size) {
          this.markCommunicationSuccessful(details);
        }
      }
    } else {
      this.storage.removeItem(key);

      if (ackKeyDetails.notificationNumber == this.maxRetries) {
        let maxDuration = this.tryIntervalTime * this.maxRetries;
        let handshakeDuration = now - ackKeyDetails.originatingTimeStamp;
        let message: string;
        if (handshakeDuration > maxDuration) {
          // Scenario where a message is considered "timed out", but we are receiving an ack for it.
          message = "wcb event ack received much later than expected max duration of " + maxDuration;
        } else {
          // This can happen in scenarios where a previous ack was successful, removing the publish details.
        }

        if (message) {
          this.pushTelemetryLog({
            message: message,
            handshakeDuration,
            originalTimeStamp: ackKeyDetails.originatingTimeStamp,
            now,
            ackTimeStamp: ackKeyDetails.timeStamp,
            ackWindowId: ackKeyDetails.windowId,
            senderWindowId: ackKeyDetails.originatingWindowId
          });
        }
      }
    }
  }

  private getWindowAcknowledgementPromise(
    detail: PublishDetail,
    activeWindowIds: string[],
    tryCount: number
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      detail.promiseAckReject = reject;
      detail.promiseAckResolve = resolve;
      if (activeWindowIds.length == 0) {
        this.telemetryStatistics[detail.id].communicationSucceededDueToNoWindows = true;
        this.markCommunicationSuccessful(detail);
      }
      this.telemetryStatistics[detail.id].messageAttempts++;
      let windowNotifyKeys = activeWindowIds.map((windowId) => {
        return this.writeWindowNotification(windowId, detail, tryCount);
      });
      detail.notifiedWindowIds = new Set<string>(activeWindowIds);
      detail.currentTryAckReceptionWindowIds.clear();
      detail.windowNotifyKeys = detail.windowNotifyKeys
        ? detail.windowNotifyKeys.concat(windowNotifyKeys)
        : windowNotifyKeys;
      let checkRetry = () => {
        if (detail.currentTryAckReceptionWindowIds.size != detail.notifiedWindowIds.size) {
          this.telemetryStatistics[detail.id].totalFailedNotifications +=
            detail.notifiedWindowIds.size - detail.currentTryAckReceptionWindowIds.size;
          reject("Timed out while waiting for notification acks");
        }
      };
      window.setTimeout(checkRetry.bind(this), this.tryIntervalTime);
    }).catch((error) => {
      if (tryCount < this.maxRetries) {
        return this.getWindowAcknowledgementPromise(detail, this.getActiveUnresponsiveWindowIds(detail), tryCount + 1);
      }
      this.telemetryStatistics[detail.id].communicationSucceeded = false;
      let errorMessage = `Max retries met, unresponded events for ${detail.eventName}`;
      this.pushTelemetryError(errorMessage, error);
      this.cleanUpAllEntriesForEvent(detail);
      return Promise.reject(new Error(errorMessage + " " + error));
    });
  }

  private getWindowResultsPromise(detail: PublishDetail, activeWindowIds: string[]): Promise<SubscriberResults> {
    return new Promise<SubscriberResults>((resolve, reject) => {
      detail.promiseResultResolve = resolve;
      activeWindowIds.forEach((wId) => detail.awaitingResultsWindowIds.add(wId));
      if (!detail.awaitingResultsTimeout) {
        detail.awaitingResultsTimeout = window.setTimeout(
          () => reject("Timed out while waiting for results"),
          resultsWaitTime
        );
      }
    });
  }

  private notifyThroughStorage(detail: PublishDetail, tryCount: number): PublishResult {
    let activeWindowIds: string[];
    let error: any;
    try {
      activeWindowIds = this.getActiveUnresponsiveWindowIds(detail);
    } catch (e) {
      error = e;
    }
    let resultsPromise: Promise<SubscriberResults>;
    if (detail.options.waitForResults) {
      resultsPromise = error ? <any>Promise.reject(error) : this.getWindowResultsPromise(detail, activeWindowIds);
    }
    let ackPromise: Promise<void> = error
      ? Promise.reject(error)
      : this.getWindowAcknowledgementPromise(detail, activeWindowIds, 1);
    return {
      messageAcknowledged: ackPromise,
      callbackResultsPromise: resultsPromise
    };
  }

  private cleanUpAllEntriesForEvent(detail: PublishDetail) {
    this.telemetryStatistics[detail.id].acknowledgedWindowIds = Array.from(detail.allAckReceptionWindowIds);
    this.telemetryStatistics[detail.id].done = true;
    let eventKeys = Array.from(detail.ackReceptionKeys);
    eventKeys.push(detail.mainEventKey);
    eventKeys = eventKeys.concat(detail.windowNotifyKeys).concat(detail.resultsReceptionKeys);
    this.cleanUpEntries(eventKeys);
    delete this.publishDetails[detail.id];
  }

  private cleanUpEntries(keys: string[]) {
    keys.forEach((key) => {
      this.storage.removeItem(key);
    });
  }

  private createMainEvent(eventName: string, params: any, options: CommunicationOptions) {
    let timeStamp = new Date().getTime();
    let uniqueId = v4();
    let mainEventKey = `${windowEventPrefix}_${this.windowId}_${timeStamp}_${uniqueId}`;
    let storageObj: StorageObject = { params: params, eventName: eventName, options: options };
    let paramObj = JSON.stringify(storageObj);
    this.storage.setItem(mainEventKey, paramObj);
    let detail = new PublishDetail(uniqueId, mainEventKey, eventName, options);
    this.publishDetails[uniqueId] = detail;
    this.telemetryStatistics[uniqueId] = new TelemetryStatistics(
      eventName,
      paramObj.length,
      this.storageNotificationMethod,
      options
    );
    let eventSize = this.toKB(paramObj.length + eventName.length);
    if (eventSize > maxEventSize) {
      let message = `Message for ${eventName} exceeded max message size of ${maxEventSize} KB. Actual Size: ${eventSize} KB. Params: ${JSON.stringify(
        params
      )}`;
      this.pushTelemetryLog(message);
    }
    return detail;
  }

  private writeWindowNotification(windowId: string, detail: PublishDetail, notificationNumber: number): string {
    this.telemetryStatistics[detail.id].totalNotifications++;
    let timeStamp = new Date().getTime();
    let windowNotifyKey = this.createWindowNotificationKey(
      windowId,
      timeStamp,
      detail.id,
      this.windowId,
      detail.dateCreated.getTime(),
      notificationNumber
    );
    this.storage.setItem(windowNotifyKey, detail.mainEventKey);
    return windowNotifyKey;
  }

  private createWindowNotificationKey(
    notifiedWindowId: string,
    timeNotificationCreated: number,
    messageId: string,
    notifierWindowId: string,
    publishDetailCreationTime: number,
    notificationNumber: number
  ) {
    return `${windowNotificationPrefix}_${notifiedWindowId}_${timeNotificationCreated}_${messageId}_${notifierWindowId}_${publishDetailCreationTime}_${notificationNumber}`;
  }

  private getActiveUnresponsiveWindowIds(details: PublishDetail): string[] {
    let myWindowId = this.windowsHeartbeatService.getCurrentWindowId();
    let windows = this.windowsHeartbeatService.getWindows();
    if (details.options.targetedWindowIds) {
      let newWindowIds: WindowStatus[] = [];
      details.options.targetedWindowIds.forEach((wId) => {
        let targetedWindowFound = windows.find((w) => w.windowId == wId);
        if (!targetedWindowFound && !details.allAckReceptionWindowIds.has(wId)) {
          let errorMessage = `Unable to target communication for window: ${wId}. Event: ${details.eventName}`;
          throw new Error(errorMessage);
        }
        newWindowIds.push(targetedWindowFound);
      });
      windows = newWindowIds;
    }
    return windows
      .filter((window) => window.windowId != myWindowId)
      .filter((window) => {
        switch (window.status) {
          case open:
            return true;
          case closed:
          case unexpectedlyClosed:
            return false;
          default:
            throw Error("Unexpected status type " + window.status);
        }
      })
      .filter((window) => {
        if (
          details.notifiedWindowIds &&
          details.notifiedWindowIds.size > 0 &&
          !details.notifiedWindowIds.has(window.windowId)
        ) {
          return false;
        }
        return !details.allAckReceptionWindowIds.has(window.windowId);
      })
      .map((window) => window.windowId);
  }

  private executeCallbacks(eventName: string, params: any): Promise<SubscriberResults> {
    let promises: Promise<any>[] = [];
    let callbacks = this.subscriptions[eventName];
    let results: SubscriberResults = { subscribers: callbacks ? callbacks.length : 0 };
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          let callbackResult = callback(params);
          if (callbackResult && callbackResult.then && callbackResult.catch) {
            promises.push(callbackResult.catch((error: any) => this.collectErrorResults(results, eventName, error)));
          }
        } catch (e) {
          this.collectErrorResults(results, eventName, e);
        }
      });
    }
    if (promises.length > 0) {
      return Promise.all(promises).then(() => Promise.resolve(results));
    }
    return Promise.resolve(results);
  }

  private collectErrorResults(results: SubscriberResults, eventName: string, error: Error) {
    if (!results.errors) {
      results.errors = [];
    }
    this.pushTelemetryError(`Error calling callback for event ${eventName}.`, error);
    results.errors.push(error.toString().substring(0, callbackErrorMaxLength));
  }

  public subscribe(eventName: string, callback: (params: any) => any, $scope: ng.IScope): () => void {
    if (!this.subscriptions[eventName]) {
      this.subscriptions[eventName] = [];
    }
    let unsubscribeDelegate = () => {
      this.unsubscribe(eventName, callback);
    };
    this.subscriptions[eventName].push(callback);
    if ($scope) {
      $scope.$on("$destroy", unsubscribeDelegate.bind(this));
    }
    return unsubscribeDelegate.bind(this);
  }

  public unsubscribe(eventName: string, callback: (params: any) => any) {
    if (this.subscriptions[eventName] && this.subscriptions[eventName].includes(callback)) {
      this.subscriptions[eventName].splice(this.subscriptions[eventName].indexOf(callback), 1);
    }
  }

  public getWindowId(): string {
    return this.windowsHeartbeatService.getCurrentWindowId();
  }

  private pushTelemetryLog(message: any): void {
    if (typeof message == "string") {
      message = "Window Communication Bridge: " + message;
    } else if (typeof message == "object") {
      message.telemetrySource = "Window Communication Bridge";
      message = JSON.stringify(message);
    }
    try {
      nonStandardPushInformationEvent("window-communication-bridge", message);
    } catch (error) {
      console.error(`not able to log to asimov during service desk eventing. Error: ${error}. Log Message: ${message}`);
    }
  }

  private pushTelemetryError(message: string, error?: any) {
    let messageToLog = `${message}  ${error ? error.stack || error : ""}`;
    try {
      nonStandardPushErrorEvent("window-communication-bridge", messageToLog, { isUnhandled: false });
    } catch (caughtError) {
      console.error(
        `not able to log to asimov during service desk eventing. Error: ${caughtError}. Log Message: ${message}`
      );
    }
  }
}
export class WindowCommunicationBridgeStandalone
  extends WindowCommunicationBridge
  implements StandaloneWindowCommunicationBridgeService
{
  constructor(storage: Storage, pollForEvents: boolean) {
    super(storage, WindowHeartbeatServiceInstance(), undefined, pollForEvents);
  }
  public subscribe(eventName: string, callback: (params: any) => any, $scope?: ng.IScope): () => void {
    // Note: Standalone instance is currently being used in cases where it maybe shouldn't be.
    return super.subscribe(eventName, callback, $scope);
  }
}
